import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import ExplicitBlur from "../components/ExplicitBlur";

export default function Gallery() {
    const [prints, setPrints] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get("/prints").then((r) => setPrints(r.data)).finally(() => setLoading(false));
    }, []);

    // Split by orientation so landscape works group together and don't unbalance the grid
    const { portraits, landscapes } = useMemo(() => {
        const portraits = prints.filter((p) => (p.orientation || "portrait") !== "landscape");
        const landscapes = prints.filter((p) => p.orientation === "landscape");
        return { portraits, landscapes };
    }, [prints]);

    // Primary year used for the active category chip (works are grouped by year once we have multiple)
    const activeYear = useMemo(() => {
        const years = prints.map((p) => p.release_year).filter(Boolean);
        return years.length ? Math.max(...years) : null;
    }, [prints]);

    const PortraitCard = ({ p }) => (
        <Link
            to={`/print/${p.slug}`}
            data-testid={`gallery-print-${p.id}`}
            className="group block"
        >
            <div className="relative aspect-[3/4] bg-[#F0EDE5] border border-black/[0.08] overflow-hidden mb-5">
                <div className="absolute inset-0 flex items-center justify-center p-5 md:p-10">
                    <ExplicitBlur
                        isExplicit={p.is_explicit}
                        src={fileUrl(p.image_url || p.image_path)}
                        alt={p.title}
                        className="w-full h-full group-hover:scale-[1.02] transition-transform duration-700"
                        testId={`gallery-reveal-${p.id}`}
                        fit="contain"
                    />
                </div>
            </div>
            <div className="label-caps text-[#8A8A8A] mb-2">
                {p.medium}{p.collection && ` · ${p.collection}`}
            </div>
            <div className="font-serif-display text-xl md:text-2xl text-[#1A1A1A] mb-1 group-hover:text-[#8C3A3A] transition-colors">
                {p.title}
            </div>
            <div className="text-sm text-[#5A5A5A]">from A${p.price.toFixed(0)}</div>
        </Link>
    );

    const LandscapeCard = ({ p }) => (
        <Link
            to={`/print/${p.slug}`}
            data-testid={`gallery-print-${p.id}`}
            className="group block"
        >
            <div className="relative aspect-[4/3] bg-[#F0EDE5] border border-black/[0.08] overflow-hidden mb-5">
                <div className="absolute inset-0 flex items-center justify-center p-5 md:p-10">
                    <ExplicitBlur
                        isExplicit={p.is_explicit}
                        src={fileUrl(p.image_url || p.image_path)}
                        alt={p.title}
                        className="w-full h-full group-hover:scale-[1.02] transition-transform duration-700"
                        testId={`gallery-reveal-${p.id}`}
                        fit="contain"
                    />
                </div>
            </div>
            <div className="label-caps text-[#8A8A8A] mb-2">
                {p.medium}{p.collection && ` · ${p.collection}`}
            </div>
            <div className="font-serif-display text-xl md:text-2xl text-[#1A1A1A] mb-1 group-hover:text-[#8C3A3A] transition-colors">
                {p.title}
            </div>
            <div className="text-sm text-[#5A5A5A]">from A${p.price.toFixed(0)}</div>
        </Link>
    );

    return (
        <div className="relative z-10 pt-24 sm:pt-32 pb-24" data-testid="gallery-page">
            <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16">
                <div className="mb-10 sm:mb-14 fade-up">
                    <div className="label-caps text-[#8C3A3A] mb-4">The Archive</div>
                    <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter text-[#1A1A1A]">
                        Prints
                    </h1>
                </div>

                {/* Category chip row — single category for now (year). Future: year, series, medium */}
                {activeYear && (
                    <div className="flex flex-wrap gap-3 mb-10 sm:mb-16" data-testid="category-chips">
                        <button
                            type="button"
                            data-testid={`category-year-${activeYear}`}
                            className="px-6 py-2 text-xs tracking-[0.2em] uppercase border bg-[#1A1A1A] text-[#FAFAF7] border-[#1A1A1A]"
                        >
                            {activeYear}
                        </button>
                    </div>
                )}

                {loading ? (
                    <div className="text-[#5A5A5A] label-caps">Loading works...</div>
                ) : prints.length === 0 ? (
                    <div className="text-[#5A5A5A] font-serif-display text-2xl italic">No works yet.</div>
                ) : (
                    <div className="space-y-20">
                        {/* Portraits — uniform 3-col grid */}
                        {portraits.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12" data-testid="portraits-grid">
                                {portraits.map((p) => (
                                    <PortraitCard key={p.id} p={p} />
                                ))}
                            </div>
                        )}

                        {/* Landscapes — grouped together in a wider 2-col grid so they don't disrupt the portrait flow */}
                        {landscapes.length > 0 && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12" data-testid="landscapes-grid">
                                {landscapes.map((p) => (
                                    <LandscapeCard key={p.id} p={p} />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
